import { supabase } from '../supabaseClient';

/**
 * Formats a message template with appointment variables.
 * If the professional has a branch assigned, uses that branch's data for {sede_*} variables.
 * If the professional has a custom wa_template, that overrides the global template.
 */
export const getWhatsAppMessageText = (app, template, branchData) => {
  const defaultTemplate = 'Hola {paciente}, te recordamos tu turno para el día {fecha} a las {hora} hs. Por favor, confirma tu asistencia ingresando aquí: {link_confirmacion}';
  // Use professional's custom template if available, else use the passed template, else global, else default
  const activeTemplate = app._pro_wa_template || template || localStorage.getItem('gdt_wa_auto_template') || defaultTemplate;
  
  const origin = window.location.origin;
  const pathname = window.location.pathname.replace(/\/$/, '');
  const link = `${origin}${pathname}/#/confirmar-turno/${app.id}`;
  const cancelLink = `${origin}${pathname}/#/cancelar-turno/${app.id}`;

  // Branch data: from injected branchData, or from app's study_details, or from professional's branch
  const branch = branchData || app._branch || null;
  const sedeNombre = branch?.name || app.study_details?.branch_name || app.consultorio_name || 'Sede Central';
  const sedeDireccion = branch?.address || app.study_details?.branch_address || 'Bartolome Mitre 854. CABA';
  const sedeEmail = branch?.email || app.study_details?.branch_email || 'gestionamientodeturnos@gmail.com';
  const sedeTelefono = branch?.phone || app.study_details?.branch_phone || '1125565076';
  
  return activeTemplate
    .replace(/{paciente}/g, app.patient_name || 'Paciente')
    .replace(/{fecha}/g, app.appointment_date || '')
    .replace(/{hora}/g, app.appointment_time?.substring(0, 5) || '')
    .replace(/{sede}/g, sedeNombre)
    .replace(/{sede_nombre}/g, sedeNombre)
    .replace(/{sede_direccion}/g, sedeDireccion)
    .replace(/{sede_email}/g, sedeEmail)
    .replace(/{sede_telefono}/g, sedeTelefono)
    .replace(/{profesional}/g, app.professional_name || 'GDT')
    .replace(/{estudio}/g, app.study_type || 'CONSULTA')
    .replace(/{link_confirmacion}/g, link)
    .replace(/{link_cancelacion}/g, cancelLink)
    .replace(/\[\[WHATSAPP\]\]/g, sedeTelefono)
    .replace(/{link_meet}/g, app.meet_link || 'No aplica')
    .replace(/{mensaje_personalizado_turno}/g, app.notes || '')
    .replace(/{mensaje_tipo_turno}/g, app.study_type ? `Turno para: ${app.study_type}` : '')
    .replace(/{datos_extra}/g, app.admin_notes || '')
    .replace(/{mensaje_profesional}/g, app._pro_message || '');
};

/**
 * Resolves professional's branch and custom template from DB, then enriches the app object.
 */
export const enrichAppWithProfessionalData = async (app) => {
  try {
    if (!app.professional_name) return app;
    const { data } = await supabase
      .from('professionals')
      .select('branch_id, wa_template')
      .eq('full_name', app.professional_name)
      .single();
    
    if (!data) return app;
    
    const enriched = { ...app };
    if (data.wa_template) enriched._pro_wa_template = data.wa_template;
    
    if (data.branch_id) {
      const { data: branch } = await supabase
        .from('branches')
        .select('*')
        .eq('id', data.branch_id)
        .single();
      if (branch) enriched._branch = branch;
    }
    
    return enriched;
  } catch (e) {
    console.error('enrichAppWithProfessionalData error:', e);
    return app;
  }
};



/**
 * Sends a WhatsApp notification using either the configured API or manual fallback (wa.me).
 * Logs the result into the database.
 */
export const sendWhatsAppNotification = async (app, template, addNotification) => {
  const provider = localStorage.getItem('gdt_wa_auto_provider') || 'evolution';
  const endpoint = localStorage.getItem('gdt_wa_auto_endpoint') || 'https://161-35-58-64.nip.io/message/sendText/GDTClinicDefinitivo';
  const token = localStorage.getItem('gdt_wa_auto_token') || 'GDTSecretKey2026';
  
  // If template is already a pre-formatted message (doesn't contain placeholders), use it directly
  const message = (typeof template === 'string' && !template.includes('{paciente}'))
    ? template
    : getWhatsAppMessageText(app, template);
  const phone = app.patient_phone?.replace(/\D/g, '') || '';
  
  if (!phone) {
    if (addNotification) addNotification('Error: El paciente no tiene un número de teléfono válido.', 'error');
    return false;
  }
  
  const logPayload = {
    appointment_id: app.id,
    patient_name: app.patient_name,
    patient_phone: phone,
    message: message,
    sent_at: new Date().toISOString()
  };
  
  // 1. Manual / Simulator Mode
  if (provider === 'simulator') {
    const encodedMsg = encodeURIComponent(message);
    const url = `https://wa.me/${phone}?text=${encodedMsg}`;
    window.open(url, '_blank');
    
    try {
      logPayload.status = 'success';
      await supabase.from('whatsapp_logs').insert([logPayload]);
      
      await supabase.from('appointments').update({
        verification_status: 'sent',
        whatsapp_reminder_sent: true,
        whatsapp_reminder_sent_at: new Date().toISOString(),
        whatsapp_reminder_status: 'sent'
      }).eq('id', app.id);
    } catch (dbErr) {
      console.error("Database log error:", dbErr);
    }
    
    if (addNotification) addNotification('Enlace de WhatsApp Web abierto con éxito', 'success');
    return true;
  }
  
  // 2. Real API Integrations
  if (!endpoint) {
    if (addNotification) addNotification('Error: No se ha configurado la URL Endpoint de la API.', 'error');
    // Fallback to manual wa.me link
    const encodedMsg = encodeURIComponent(message);
    const url = `https://wa.me/${phone}?text=${encodedMsg}`;
    window.open(url, '_blank');
    return false;
  }
  
  try {
    let response;
    const headers = { 'Content-Type': 'application/json' };
    let body;
    
    if (provider === 'evolution') {
      headers['apikey'] = token;
      body = JSON.stringify({
        number: phone,
        text: message
      });
      
      response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body
      });
    } else if (provider === 'meta') {
      headers['Authorization'] = `Bearer ${token}`;
      body = JSON.stringify({
        messaging_product: "whatsapp",
        recipient_type: "individual",
        to: phone,
        type: "text",
        text: {
          preview_url: false,
          body: message
        }
      });
      
      response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body
      });
    } else if (provider === 'twilio') {
      headers['Content-Type'] = 'application/x-www-form-urlencoded';
      
      let credentials = token;
      // AccountSid:AuthToken encoding if contains colon
      if (token.includes(':')) {
        credentials = btoa(token);
      }
      headers['Authorization'] = `Basic ${credentials}`;
      
      const twilioFrom = localStorage.getItem('gdt_wa_twilio_from') || 'whatsapp:+14155238886';
      
      const params = new URLSearchParams();
      params.append('To', `whatsapp:+${phone}`);
      params.append('From', twilioFrom);
      params.append('Body', message);
      
      response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: params.toString()
      });
    }
    
    if (response && response.ok) {
      logPayload.status = 'success';
      await supabase.from('whatsapp_logs').insert([logPayload]);
      
      await supabase.from('appointments').update({
        verification_status: 'sent',
        whatsapp_reminder_sent: true,
        whatsapp_reminder_sent_at: new Date().toISOString(),
        whatsapp_reminder_status: 'sent',
        whatsapp_reminder_error: null
      }).eq('id', app.id);
      
      if (addNotification) addNotification('Notificación enviada por WhatsApp API con éxito', 'success');
      return true;
    } else {
      const errorText = response ? await response.text() : 'Error de red / API sin respuesta';
      throw new Error(errorText);
    }
  } catch (err) {
    console.error("API Send Failure:", err);
    
    // Log failure in Database
    try {
      logPayload.status = 'failed';
      logPayload.error = err.message || 'Error de conexión con API';
      await supabase.from('whatsapp_logs').insert([logPayload]);
      
      await supabase.from('appointments').update({
        whatsapp_reminder_status: 'failed',
        whatsapp_reminder_error: err.message || 'Fallo de API'
      }).eq('id', app.id);
    } catch (dbErr) {
      console.error("Database log error:", dbErr);
    }
    
    // Fallback: Show warning and open manual wa.me link
    if (addNotification) {
      addNotification(`Fallo al enviar por API (${err.message}). Abriendo WhatsApp Web de respaldo...`, 'warning');
    }
    
    const encodedMsg = encodeURIComponent(message);
    const url = `https://wa.me/${phone}?text=${encodedMsg}`;
    window.open(url, '_blank');
    return false;
  }
};
